import * as THREE from 'three';
import { LocalCoordinates } from '@/types/course.types';
import { PlayerInfo } from '@/types/shot.types';
import { BaseShotTracer } from './BaseShotTracer';

/**
 * Shot tracer using TubeGeometry for 3D tube rendering
 * This version creates a 3D tube with lighting and shadows
 */
export class TubeShotTracer extends BaseShotTracer {
  private tubeMaterial: THREE.MeshStandardMaterial;
  private tubeMesh: THREE.Mesh | null = null;
  private tubeRadius: number;

  constructor(
    start: LocalCoordinates,
    end: LocalCoordinates,
    apexHeight: number,
    color: number,
    playerInfo: PlayerInfo,
    tubeRadius: number = 2
  ) {
    super(start, end, apexHeight, color, playerInfo);

    this.tubeRadius = tubeRadius;

    // Material for the tube
    this.tubeMaterial = new THREE.MeshStandardMaterial({
      color: color,
      metalness: 0.3,
      roughness: 0.7,
    });

    // Initial tube
    this.updatePath();
  }

  /**
   * Update the tube path based on animation progress
   */
  protected updatePath(): void {
    // Remove existing mesh
    if (this.tubeMesh) {
      this.tubeMesh.geometry.dispose();
      this.tubeMesh = null;
    }

    // Get points for current progress
    const points = this.curve.getPoints(50);
    const numPoints = Math.max(
      2,
      Math.floor(points.length * this.animationState.progress)
    );
    const partialPoints = points.slice(0, numPoints);

    // Create partial curve
    const partialCurve = new THREE.CatmullRomCurve3(partialPoints);

    // Create tube geometry
    const tubeGeometry = new THREE.TubeGeometry(
      partialCurve,
      Math.max(2, numPoints - 1), // tubular segments
      this.tubeRadius, // radius
      8, // radial segments
      false // closed
    );

    // Create new mesh
    this.tubeMesh = new THREE.Mesh(tubeGeometry, this.tubeMaterial);
    this.tubeMesh.castShadow = true;
  }

  /**
   * Get the tube mesh for adding to scene
   */
  public getPathObject(): THREE.Object3D {
    return this.tubeMesh || new THREE.Object3D();
  }

  /**
   * Clean up resources
   */
  public dispose(): void {
    if (this.tubeMesh) {
      this.tubeMesh.geometry.dispose();
    }
    this.tubeMaterial.dispose();
    this.golfBall.dispose();
  }
}
